import { Component } from '@theme/component';

const SECOND = 1000;
const MINUTE = 60 * SECOND;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;
const COPY_FEEDBACK_MS = 1500;
/** setTimeout's ceiling (~24.8 days); a later start simply needs a reload. */
const MAX_TIMEOUT = 2 ** 31 - 1;

/**
 * Parses a "YYYY-MM-DD HH:MM:SS" block setting as the visitor's local time.
 *
 * @param {string | undefined} value
 * @returns {number} Epoch milliseconds, or NaN when blank or malformed.
 */
function parseSettingDate(value) {
  return new Date((value ?? '').replace(' ', 'T')).getTime();
}

/**
 * @param {number} value
 * @returns {string}
 */
function pad(value) {
  return value.toString().padStart(2, '0');
}

/**
 * Limited-offer banner: hidden until its start date, then counts down to the end
 * date. The dates are checked in the browser because the rendered page is cached.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} days
 * @property {HTMLElement} hours
 * @property {HTMLElement} minutes
 * @property {HTMLElement} seconds
 *
 * @extends {Component<Refs>}
 */
class LimitedOfferComponent extends Component {
  requiredRefs = ['days', 'hours', 'minutes', 'seconds'];

  #endDate = NaN;

  /** @type {number | undefined} */
  #interval;

  /** @type {number | undefined} */
  #feedbackTimeout;

  /** @type {number | undefined} */
  #startTimeout;

  connectedCallback() {
    super.connectedCallback();

    // Before the start date: stay hidden, but show up on time if the page is
    // still open then (a cached page may be opened before the campaign starts).
    const untilStart = parseSettingDate(this.dataset.startDate) - Date.now();
    if (untilStart > 0) {
      this.style.display = 'none';
      if (untilStart <= MAX_TIMEOUT) {
        this.#startTimeout = window.setTimeout(() => {
          this.style.display = '';
          this.#start();
        }, untilStart);
      }
      return;
    }

    this.#start();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.clearInterval(this.#interval);
    window.clearTimeout(this.#feedbackTimeout);
    window.clearTimeout(this.#startTimeout);
  }

  #start() {
    this.#endDate = parseSettingDate(this.dataset.endDate);
    this.#tick();
    this.#interval = window.setInterval(() => this.#tick(), SECOND);
  }

  /**
   * Copies the coupon code and briefly swaps the button label for a confirmation.
   *
   * @param {Event} event - Click on the coupon button.
   */
  async copyCoupon(event) {
    const button = event.target;
    if (!(button instanceof HTMLButtonElement)) return;

    const { couponCode, couponText, successText } = button.dataset;
    if (!couponCode) return;

    try {
      await navigator.clipboard.writeText(couponCode);
    } catch (error) {
      console.error('Clipboard copy failed:', error);
      return;
    }

    button.textContent = successText ?? '';
    window.clearTimeout(this.#feedbackTimeout);
    this.#feedbackTimeout = window.setTimeout(() => {
      button.textContent = couponText ?? '';
    }, COPY_FEEDBACK_MS);
  }

  #tick() {
    const { days, hours, minutes, seconds } = this.refs;
    const distance = this.#endDate - Date.now();

    // An expired offer keeps showing a zeroed timer rather than hiding itself.
    if (distance < 0) {
      days.textContent = '0';
      hours.textContent = '00';
      minutes.textContent = '00';
      seconds.textContent = '00';
      return;
    }

    days.textContent = String(Math.floor(distance / DAY));
    hours.textContent = pad(Math.floor((distance % DAY) / HOUR));
    minutes.textContent = pad(Math.floor((distance % HOUR) / MINUTE));
    seconds.textContent = pad(Math.floor((distance % MINUTE) / SECOND));
  }
}

if (!customElements.get('limited-offer-component')) {
  customElements.define('limited-offer-component', LimitedOfferComponent);
}
